import { FC, useEffect, useRef, useState } from "react";
import { Page } from "../../../Components/Page";
import {
  Center,
  Loader,
  Title,
  Button,
  Alert,
  Grid,
  ScrollArea,
  Space,
  Box,
  Flex,
  Switch,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import {
  gameFreezeRegularTime,
  gamePauseClock,
  gameResumeClock,
  gameStart,
  troetHorn,
} from "../../../openapi/api-client";
import { TimeButton } from "./Components/TimeButton";
import { GoalButton } from "./Components/GoalButton";
import { GameTimeline } from "./Components/GameTimeline";
import { TeamName } from "../../../Components/TeamName";
import { useLocalStorage, useViewportSize } from "@mantine/hooks";
import { IconVolume, IconClock } from "@tabler/icons-react";
import { TimePenaltiesPanel } from "./Components/TimePenaltiesPanel";
import { TeamLogo } from "../../../Components/TeamLogo";
import { useGameTime } from "../../../hooks/useGameTime";
import { LinksForm } from "./Components/LinksForm";
import { useGame } from "../../../hooks/useGame.ts";
import { SetActualTeamsModal } from "./SetActualTeamsModal";
import { SetClockModal } from "./SetClockModal";
import { ResolvedTeamReference } from "../../../Components/ResolvedTeamReference";
import { useNavigate } from "react-router-dom";
import { ShootOutSection } from "./Components/ShootOutSection";

interface GameDetailProps {
  gameId: string;
}

export const GameDetail: FC<GameDetailProps> = (props) => {
  const { gameId } = props;

  const game = useGame(gameId);
  const viewport = useViewportSize();
  const navigate = useNavigate();
  const [showClockModal, setShowClockModal] = useState(false);

  const time = useGameTime(gameId);
  const [skipScorerModal, setSkipScorerModal] = useLocalStorage({
    key: "skipScorerModal",
    defaultValue: false,
  });

  // Refs for keyboard-triggered goals
  const homeGoalTrigger = useRef<(() => void) | undefined>();
  const guestGoalTrigger = useRef<(() => void) | undefined>();

  const startGameMutation = useMutation({ mutationFn: gameStart });
  const resumeGameMutation = useMutation({
    mutationFn: async (arg: { id: string }) => {
      try {
        return await gameResumeClock(arg);
      } catch (e) {
        // Ignore expected errors (e.g. clock already running due to double-click)
        console.warn("resumeClock failed:", e);
        return;
      }
    },
  });
  const pauseGameMutation = useMutation({
    mutationFn: async (arg: { id: string }) => {
      try {
        return await gamePauseClock(arg);
      } catch (e) {
        // Ignore expected errors (e.g. clock already paused due to double-click)
        console.warn("pauseClock failed:", e);
        return;
      }
    },
  });
  const freezeRegularTimeMutation = useMutation({
    mutationFn: gameFreezeRegularTime,
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key repeats (holding down key)
      if (e.repeat) return;

      // Ignore if typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Ignore if a modal is open (check for overlay which only exists when modal is visible)
      if (document.querySelector(".mantine-Modal-overlay")) {
        return;
      }

      // Arrow Left = Home goal
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        homeGoalTrigger.current?.();
        return;
      }

      // Arrow Right = Guest goal
      if (e.key === "ArrowRight") {
        e.preventDefault();
        guestGoalTrigger.current?.();
        return;
      }

      // Space = Pause/Resume
      if (e.key === " " && game.isSuccess) {
        e.preventDefault();
        if (!game.data.started || game.data.regularTimeFreezed) return;

        if (game.data.clockRunning) {
          void pauseGameMutation.mutateAsync({ id: gameId });
        } else {
          void resumeGameMutation.mutateAsync({ id: gameId });
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    game.isSuccess,
    game.data,
    gameId,
    pauseGameMutation,
    resumeGameMutation,
  ]);

  if (!gameId || !game.isSuccess) {
    return <Loader />;
  }

  const needsTeamSelection =
    !game.data.actualTeams.homeTeamId || !game.data.actualTeams.guestTeamId;

  const isGameDisabled = !game.data.started || game.data.regularTimeFreezed;

  return (
    <Page>
      {needsTeamSelection && (
        <SetActualTeamsModal
          game={game.data}
          onClose={() => {
            void navigate(`/control/competitions/${game.data.competitionId}`);
          }}
        />
      )}

      <Grid gutter="xl">
        <Grid.Col span={8}>
          <Title ta="center" mb="md">
            {game.data.actualTeams.homeTeamId ? (
              <TeamName teamId={game.data.actualTeams.homeTeamId} />
            ) : (
              <ResolvedTeamReference
                teamReference={game.data.plannedTeams.home}
                competitionId={game.data.competitionId}
              />
            )}
            <> vs. </>
            {game.data.actualTeams.guestTeamId ? (
              <TeamName teamId={game.data.actualTeams.guestTeamId} />
            ) : (
              <ResolvedTeamReference
                teamReference={game.data.plannedTeams.guest}
                competitionId={game.data.competitionId}
              />
            )}
          </Title>

          {game.data.result && (
            <Alert color="green" title="Spiel beendet" variant="filled" mb="lg">
              An diesem Spiel können keine Änderungen mehr vorgenommen werden.
            </Alert>
          )}

          {game.data.result && (
            <>
              <LinksForm gameId={gameId} />
              <Space h="xl" />
            </>
          )}

          {!game.data.shootOutStarted && (
            <>
              <Grid mb="xs" align="center">
                <Grid.Col span={2}>
                  <Flex justify="center">
                    <Box w={100}>
                      <TeamLogo
                        height={100}
                        teamId={game.data.actualTeams.homeTeamId}
                      />
                    </Box>
                  </Flex>
                </Grid.Col>
                <Grid.Col span={8}>
                  <TimeButton
                    time={time}
                    regieReady={game.data.livestreamRegieReady}
                    running={game.data.clockRunning}
                    started={game.data.started}
                    freezed={game.data.regularTimeFreezed}
                    regularTimeOver={game.data.regularTimeOver}
                    start={async () =>
                      await startGameMutation.mutateAsync({ id: gameId })
                    }
                    pause={async () =>
                      await pauseGameMutation.mutateAsync({ id: gameId })
                    }
                    resume={async () =>
                      await resumeGameMutation.mutateAsync({ id: gameId })
                    }
                    freezeRegularTime={async () =>
                      await freezeRegularTimeMutation.mutateAsync({
                        id: gameId,
                      })
                    }
                  />
                </Grid.Col>
                <Grid.Col span={2} style={{ align: "right" }}>
                  <Flex justify="center">
                    <Box w={100}>
                      <TeamLogo
                        height={100}
                        teamId={game.data.actualTeams.guestTeamId}
                      />
                    </Box>
                  </Flex>
                </Grid.Col>
              </Grid>
              <Center mb="xl">
                <Button
                  variant="outline"
                  size="xs"
                  leftSection={<IconClock size={14} />}
                  onClick={() => {
                    setShowClockModal(true);
                  }}
                  disabled={
                    !game.data.started ||
                    !!game.data.result ||
                    game.data.regularTimeOver ||
                    game.data.clockRunning
                  }
                >
                  Uhr anpassen
                </Button>
              </Center>

              <Grid>
                <Grid.Col span={2}>
                  <TimePenaltiesPanel
                    teamId={game.data.actualTeams.homeTeamId}
                    gameId={game.data.id}
                    time={time}
                    timePenalties={game.data.timePenalties}
                    disabled={isGameDisabled}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <GoalButton
                    teamId={game.data.actualTeams.homeTeamId}
                    gameId={game.data.id}
                    time={time}
                    goals={game.data.score.home}
                    disabled={isGameDisabled}
                    skipScorerModal={skipScorerModal}
                    triggerRef={homeGoalTrigger}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <GoalButton
                    teamId={game.data.actualTeams.guestTeamId}
                    gameId={game.data.id}
                    time={time}
                    goals={game.data.score.guest}
                    disabled={isGameDisabled}
                    skipScorerModal={skipScorerModal}
                    triggerRef={guestGoalTrigger}
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <TimePenaltiesPanel
                    teamId={game.data.actualTeams.guestTeamId}
                    gameId={game.data.id}
                    time={time}
                    timePenalties={game.data.timePenalties}
                    disabled={isGameDisabled}
                  />
                </Grid.Col>
              </Grid>
              <Center mt="md">
                <Switch
                  label="Schnellmodus (ohne Torschütze)"
                  checked={skipScorerModal}
                  disabled={game.data.regularTimeFreezed}
                  onChange={(event) => {
                    setSkipScorerModal(!skipScorerModal);
                    event.currentTarget.blur();
                  }}
                />
              </Center>
              {!game.data.result && (
                <Grid mt="xl">
                  <Grid.Col span={2} />
                  <Grid.Col span={4}>
                    <Button.Group>
                      <Button
                        leftSection={<IconVolume />}
                        size="xl"
                        color="yellow"
                        fullWidth
                        onClick={() => void troetHorn({})}
                      >
                        Tröten
                      </Button>
                    </Button.Group>
                  </Grid.Col>
                </Grid>
              )}
            </>
          )}

          {game.data.shootOutStarted && <ShootOutSection game={game.data} />}
        </Grid.Col>

        <Grid.Col span={4}>
          <Title order={2} mb="xl">
            Spielverlauf
          </Title>
          <ScrollArea style={{ height: viewport.height - 150 }}>
            <GameTimeline gameId={gameId} />
          </ScrollArea>
        </Grid.Col>
      </Grid>

      {showClockModal && (
        <SetClockModal
          gameId={gameId}
          currentTime={time}
          onClose={() => {
            setShowClockModal(false);
          }}
        />
      )}
    </Page>
  );
};
